import { useCallback, useEffect, useRef, useState } from 'react';
import type { EventView, EntryView } from '@koto-event/shared';
import { api, apiUrl, ApiError } from '../lib/api.js';
import { useDeadlineText } from '../lib/deadline.js';
import { reauthenticate } from '../lib/liff.js';

interface Props {
  event: EventView;
  entry: EntryView;
  /** 電子スタンプが使える環境か（未設定の環境では押印枠を出さない） */
  digishotEnabled: boolean;
  /** 押印状況の確認。サーバーから最新の Entry を取り直す */
  onPoll: () => Promise<EntryView>;
  onStamped: (entry: EntryView) => void;
  /** 押印されないまま仮当選の期限が切れた */
  onExpired: () => void;
}

/** 押印待ちのポーリング間隔（ミリ秒） */
const POLL_INTERVAL_MS = 3000;
/**
 * 確定後に付与待ち（PENDING）がこの長さ続いたら、待つのをやめて演出へ進み、スタッフへの声かけを案内する。
 * 付与は確定の後にサーバーで行い、連携先の待機付き再送は最長 65 秒かかる。処理中にサーバーが止まると付与待ちのまま残るため、
 * いつまでもポーリングさせない（残った結果は管理画面から再送する）。端末とサーバーの時計のずれを避けるため画面側で数える
 */
const GRANT_WAIT_LIMIT_MS = 90000;
/** 押印画面（iframe）の準備が終わらない場合に案内へ切り替える時間 */
const PREPARE_TIMEOUT_MS = 50000;

/** iframe から届く通知。押印画面（public/stamp.js）と対になっている */
type StampMessage =
  | 'stamp-ready'
  | 'stamp-started'
  | 'stamp-recognized'
  | 'stamp-unrecognized'
  | 'stamp-blocked'
  | 'stamp-expired'
  | 'stamp-entry-expired'
  | 'stamp-auth-required'
  | 'stamp-error';

/**
 * スタッフ提示画面（フロー図「確認・押印」）。
 *
 * お客様はこの画面をスタッフに提示し、スタッフがこの画面に電子スタンプを押印する。
 * 押印は iframe に閉じ込めたベンダーの SDK が扱う（SDK に停止用の API がないため、
 * 終わったら iframe ごと破棄する）。SDK の通知は確定の根拠にせず、確定したかどうかは
 * 必ずサーバーへ問い合わせる（この画面のポーリング）。
 */
export function StaffPresentScreen({ event, entry, digishotEnabled, onPoll, onStamped, onExpired }: Props): JSX.Element {
  const [status, setStatus] = useState(entry.status);
  const [stampState, setStampState] = useState<'idle' | 'preparing' | 'ready' | 'unavailable'>(
    digishotEnabled ? 'preparing' : 'idle',
  );
  const [stampMessage, setStampMessage] = useState('');
  const [pollMessage, setPollMessage] = useState('');
  const [authenticationExpired, setAuthenticationExpired] = useState(false);
  const [pollBlocked, setPollBlocked] = useState(false);
  /** 準備し直す操作のための世代番号。増やすと iframe を作り直す */
  const [attempt, setAttempt] = useState(0);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const pendingSinceRef = useRef<number | null>(null);
  const pollInFlightRef = useRef(false);
  const remaining = useDeadlineText(entry.expiresAt);

  // --- 確定の確認（押印の手段によらず、サーバーの状態だけを信用する） ----------
  useEffect(() => {
    if (authenticationExpired || pollBlocked) return;
    let active = true;
    const poll = (): void => {
      if (!active || pollInFlightRef.current) return;
      pollInFlightRef.current = true;
      void (async () => {
        try {
          const latest = await onPoll();
          if (!active) return;
          setPollMessage('');
          setStatus(latest.status);
          // 確定しても外部システムへの付与が終わるまでは演出に進まない（付与の結果で案内を変えるため）。
          // ただし付与待ちが GRANT_WAIT_LIMIT_MS 続いたら、付与待ちのまま進む（演出の後にスタッフへの声かけを案内する）
          const waitingGrant = latest.status === 'CONFIRMED' && latest.rewardGrantStatus === 'PENDING';
          if (waitingGrant) pendingSinceRef.current ??= Date.now();
          const waitedTooLong = waitingGrant && Date.now() - (pendingSinceRef.current ?? Date.now()) >= GRANT_WAIT_LIMIT_MS;
          if (latest.status === 'CONFIRMED' && (!waitingGrant || waitedTooLong)) {
            stop();
            onStamped(latest);
          } else if (latest.status === 'EXPIRED') {
            stop();
            onExpired();
          }
        } catch (error) {
          if (!active) return;
          if (error instanceof ApiError && error.status === 401) {
            stop();
            setAuthenticationExpired(true);
            setPollMessage('LINEの認証期限が切れました。もう一度読み込んで、受取状況を確認してください。');
          } else if (error instanceof ApiError && (error.status === 403 || error.status === 404)) {
            stop();
            setPollBlocked(true);
            setPollMessage('受取状況を確認できません。受付番号をスタッフにお伝えください。');
          } else {
            setPollMessage('通信が途切れています。通信状況を確認してください。接続が戻ると受取状況を自動で確認します。');
          }
        } finally {
          pollInFlightRef.current = false;
        }
      })();
    };
    const timer = setInterval(poll, POLL_INTERVAL_MS);
    // 他のアプリから戻ってきた直後は、待たずに状態を確認する
    const onVisible = (): void => {
      if (!document.hidden) poll();
    };
    document.addEventListener('visibilitychange', onVisible);
    poll();

    function stop(): void {
      active = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    }

    return stop;
  }, [onPoll, onStamped, onExpired, authenticationExpired, pollBlocked]);

  // --- 押印（電子スタンプ） ---------------------------------------------------
  useEffect(() => {
    if (!digishotEnabled || status !== 'PROVISIONAL' || authenticationExpired || pollBlocked) return;

    let active = true;
    let prepareTimer: number | undefined;
    // サーバーが発行した押印の受付情報。iframe の準備完了を待って渡す
    let init: Record<string, unknown> | null = null;

    const onMessage = (message: MessageEvent): void => {
      const frame = frameRef.current;
      if (!active || !frame || message.origin !== window.location.origin) return;
      if (message.source !== frame.contentWindow || typeof message.data?.type !== 'string') return;
      const type = message.data.type as StampMessage;

      if (type === 'stamp-ready') {
        if (init) frame.contentWindow?.postMessage(init, window.location.origin);
        return;
      }
      if (message.data.entryId !== entry.id) return;

      if (type === 'stamp-started') {
        window.clearTimeout(prepareTimer);
        setStampState('ready');
        setStampMessage('');
      } else if (type === 'stamp-recognized') {
        // 確定したかどうかはポーリングで確認する。SDK の通知だけでは先へ進めない
        setStampMessage('押印を受け付けました。特典の確認をしています…');
      } else if (type === 'stamp-unrecognized') {
        setStampMessage(
          message.data.detail === 'STAMP_MISMATCH'
            ? 'この店舗のスタンプではないようです。スタッフにご確認ください。'
            : '押印を確認できませんでした。もう一度お試しください。',
        );
      } else if (type === 'stamp-blocked') {
        setStampState('unavailable');
        setStampMessage('公式LINEの友だち登録を確認できませんでした。登録後にもう一度お試しください。');
      } else if (type === 'stamp-expired') {
        setStampState('unavailable');
        setStampMessage('押印の準備の有効期限が切れました。もう一度準備してください。');
      } else if (type === 'stamp-entry-expired') {
        onExpired();
      } else if (type === 'stamp-auth-required') {
        setAuthenticationExpired(true);
        setPollMessage('LINEの認証を確認できませんでした。認証をやり直して、押印を準備してください。');
      } else if (type === 'stamp-error') {
        setStampState('unavailable');
        setStampMessage('電子スタンプを読み込めませんでした。');
      }
    };

    window.addEventListener('message', onMessage);

    void (async () => {
      try {
        const challenge = await api.stampChallenge(entry.id);
        if (!active) return;
        const serverNow = Date.parse(challenge.serverNow);
        init = {
          type: 'stamp-init',
          entryId: entry.id,
          // URL はサーバーが発行したものをそのまま渡す（API が別オリジンの場合だけ前置する）
          sdkUrl: apiUrl(challenge.sdkUrl),
          serviceUri: apiUrl(challenge.serviceUri),
          jqueryUrl: challenge.jqueryUrl,
          konvaUrl: challenge.konvaUrl,
          expiresAt: challenge.expiresAt,
          // 端末の時計がずれていても期限の判定を誤らないようにする
          clockOffset: Number.isFinite(serverNow) ? serverNow - Date.now() : 0,
        };
        // 準備完了の通知を取りこぼしていた場合に備えて、こちらからも一度送る
        frameRef.current?.contentWindow?.postMessage(init, window.location.origin);
        prepareTimer = window.setTimeout(() => {
          if (!active) return;
          setStampState('unavailable');
          setStampMessage('電子スタンプの準備を確認できません。');
        }, PREPARE_TIMEOUT_MS);
      } catch (error) {
        if (!active) return;
        if (error instanceof ApiError && error.status === 401) {
          setAuthenticationExpired(true);
          setPollMessage('LINEの認証期限が切れました。もう一度読み込んで、受取状況を確認してください。');
        }
        setStampState('unavailable');
        setStampMessage(
          error instanceof Error && 'code' in error && (error as { code: string }).code === 'DIGISHOT_NOT_CONFIGURED'
            ? ''
            : '押印の準備ができませんでした。',
        );
      }
    })();

    return () => {
      active = false;
      window.clearTimeout(prepareTimer);
      window.removeEventListener('message', onMessage);
    };
  }, [digishotEnabled, entry.id, attempt, status, authenticationExpired, pollBlocked, onExpired]);

  const retry = useCallback(() => {
    setStampState('preparing');
    setStampMessage('');
    setAttempt((value) => value + 1);
  }, []);

  const showFrame = digishotEnabled && status === 'PROVISIONAL' && stampState !== 'unavailable' && !authenticationExpired && !pollBlocked;

  return (
    <div className="stack">
      <div className="card present">
        {event.screens.lineGuideImageUrl && (
          <img className="hero-image" src={event.screens.lineGuideImageUrl} alt="" />
        )}
        <h1>この画面をスタッフに提示ください</h1>
        <p>{event.screens.lineGuideBody || 'スタッフが電子スタンプを押印します。'}</p>

        {showFrame ? (
          <div className="stamp-slot stamp-slot--sdk">
            {/* SDK には停止用の API がないため、押印のたびに専用文書として作り直す */}
            <iframe
              key={attempt}
              ref={frameRef}
              className="stamp-frame"
              src="/stamp.html"
              title="電子スタンプの受付"
              referrerPolicy="no-referrer"
              allow="autoplay"
            />
          </div>
        ) : (
          <div className="stamp-slot">スタンプ</div>
        )}

        {stampMessage && <p className="muted center">{stampMessage}</p>}
        {stampState === 'unavailable' && status === 'PROVISIONAL' && !authenticationExpired && !pollBlocked && (
          <button type="button" className="btn btn--ghost" onClick={retry}>
            もう一度準備する
          </button>
        )}

        {pollMessage && <p className="error" role="status">{pollMessage}</p>}
        {authenticationExpired && (
          <button type="button" className="btn btn--ghost" onClick={reauthenticate}>
            LINE認証をやり直す
          </button>
        )}

        <h2>{entry.prize?.name ?? '特典'}</h2>
        {entry.locationName && <p className="muted">{entry.locationName}</p>}
        <p className="muted">受付番号: {entry.id.slice(0, 8).toUpperCase()}</p>
        {remaining && <p className="muted">受取期限: {remaining}</p>}
        <span className="badge badge--active">{status === 'PROVISIONAL' ? '押印待ち' : '特典を付与しています'}</span>
      </div>

      <p className="muted center">
        画面を閉じた場合は、店内のココデピットにもう一度タッチすると再開できます。
      </p>
    </div>
  );
}
